import { Link, useNavigate } from "react-router-dom";
import logo from "../../assets/images/logo/logo.png";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import scrollToSection from "../../utils/scrollToSection";

// Import Font Awesome
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown, 
  faBars, 
  faTimes, 
  faEnvelope, 
  faPhone,
  faLanguage,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { 
  faFacebook, 
  faTwitter, 
  faLinkedin, 
  faInstagram 
} from '@fortawesome/free-brands-svg-icons';

// Add icons to the library
library.add(
  faChevronDown, 
  faBars, 
  faTimes, 
  faEnvelope, 
  faPhone, 
  faLanguage,
  faFacebook, 
  faTwitter, 
  faLinkedin, 
  faInstagram
);

const Navbar = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const pages = ["home", "about", "services", "faqs"];
  
  // Available languages
  const languages = [
    { code: "en", name: "English" },
    { code: "fr", name: "French" },
    { code: "es", name: "Spanish" },
    { code: "de", name: "German" },
    { code: "it", name: "Italian" },
    { code: "pt", name: "Portuguese" }
  ];

  const navRef = useRef();
  const [activeIndex, setActiveIndex] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isClicked, setIsClicked] = useState(true);
  const [showLangDropdown, setShowLangDropdown] = useState(false);

  const handleClickNavItem = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
    setIsClicked(!isClicked);
  }

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setShowLangDropdown(false);
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLangDropdown && !event.target.closest('.lang-dropdown-container')) {
        setShowLangDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLangDropdown]);

  useEffect(() => {
    const NavbarScrollActive = () => {
      var scrollTop = window.scrollY;

      if (scrollTop > 200) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    }

    window.addEventListener("scroll", NavbarScrollActive);

    // hide the menu
    setIsClicked(!isClicked);

    return () => {
      window.removeEventListener("scroll", NavbarScrollActive);
    };
  }, []);

  const clickOnBurgerIcon = () => {
    setIsClicked(!isClicked);
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    })
  }

  // Get current language name
  const getCurrentLanguageName = () => {
    const currentLang = languages.find(lang => lang.code === i18n.language);
    return currentLang ? currentLang.name : "English";
  }

  return (
    <nav className={`navbar ${isScrolled ? "active" : ""}`} ref={navRef}>
      <div className="container">
        <Link to={`/`} onClick={scrollToTop}>
          <div className="brand-logo">
            <img src={logo} alt="Company Logo" />
          </div>
        </Link>

        <ul className="m-0 p-0 hide_in_mobile">
          {pages.map((page, index) => (
            <li
              className={activeIndex === index ? "active" : ""}
              key={index}
              onClick={() => {
                handleClickNavItem(index);
                scrollToSection(page.toLowerCase());
                if (page.toLowerCase() === "home" || page.toLowerCase() === "applications") {
                  scrollToTop();
                }
              }}
            >
              <Link
                to={`${page.toLowerCase() === "home" ? "/" : page.toLowerCase() === "applications" ? `/${page.toLowerCase()}` : `/#${page.toLowerCase()}`}`}
                className="text-decoration-none text-capitalize"
              >
                {t(`navbar.${page.toLowerCase()}`)}
              </Link>
            </li>
          ))}
        </ul>

        <div className="d-flex align-items-center gap-3">
          <button className="btn btn_contact hide_in_mobile" onClick={() => scrollToSection('contact')}>
            <FontAwesomeIcon icon={faEnvelope} className="me-2" />
            Contact us
          </button>
          
          {/* Language dropdown */}
          <div className="lang-dropdown-container position-relative">
            <button 
              className="btn btn_lang" 
              onClick={() => setShowLangDropdown(!showLangDropdown)}
            >
              <FontAwesomeIcon icon={faGlobe} className="me-2" />
              {getCurrentLanguageName()} <FontAwesomeIcon icon={faChevronDown} className="ms-1" />
            </button>
            
            {showLangDropdown && (
              <div className="lang-dropdown">
                {languages.map((lang) => (
                  <div 
                    key={lang.code}
                    className={`lang-item ${i18n.language === lang.code ? 'active' : ''}`}
                    onClick={() => handleLanguageChange(lang.code)}
                  >
                    {lang.name}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        <div className="burger-icon" onClick={clickOnBurgerIcon}>
          <div className={`hamburger ${isClicked ? "is-active" : ""}`} id="hamburger-11">
            <FontAwesomeIcon icon={isClicked ? faTimes : faBars} />
          </div>
        </div>

        <div className={`mobile_menu ${isClicked ? "show" : ""}`}>
          <ul className="m-0 p-0">
            {pages.map((page, index) => (
              <li
                className={activeIndex === index ? "active" : ""}
                key={index}
                onClick={() => {
                  handleClickNavItem(index);
                  scrollToSection(page.toLowerCase());
                  if (page.toLowerCase() === "home" || page.toLowerCase() === "applications") {
                    scrollToTop();
                  }
                }}
              >
                <Link
                  to={`${page.toLowerCase() === "home" ? "/" : `/#${page.toLowerCase()}`}`}
                  className="text-decoration-none text-capitalize"
                >
                  {page.toUpperCase()}
                </Link>
              </li>
            ))}
          </ul>
          
          <button className="btn btn_contact mt-3" onClick={() => scrollToSection('contact')}> 
            Contact us
          </button>

        </div>
      </div>
    </nav>
  );
};

export default Navbar;